import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";

export default function AdminDisputes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/disputes").then((r) => setItems(r.data.items))
      .catch((e) => toast.error(getErrorMessage(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/disputes/${id}/${action}`);
      toast.success(action === "approve"
        ? "Approved — old application closed, fresh lead created in Fresh Leads"
        : "Rejected — duplicate lead discarded, existing application untouched");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-amber-600" /> Lead Disputes
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Incoming leads (CSV / WhatsApp) whose mobile already matches an existing candidate. Review each and
          Approve (close the old application, open a fresh unassigned lead) or Reject (discard the new lead).
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Check} title="No disputes to review"
          description="Duplicate leads from CSV upload or the WhatsApp webhook will appear here for your decision." />
      ) : (
        <div className="space-y-2.5">
          {items.map((d) => (
            <Card key={d.id} className="rounded-xl" data-testid="dispute-item">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <Link to={`/admin/candidates/${d.candidate_id}`} className="min-w-0 space-y-1 hover:opacity-80"
                  data-testid={`dispute-open-${d.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{d.candidate_name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{d.mobile}</span>
                    <Badge variant="outline" className="rounded-full text-xs bg-sky-50 text-sky-700 border-sky-200">{d.source}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Current stage: <span className="font-medium">{d.current_stage || "—"}</span>
                    {" · "}Owner: <span className="font-medium">{d.current_owner_name || "Unassigned"}</span>
                  </p>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" className="rounded-lg gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={busyId === d.id} onClick={() => act(d.id, "approve")} data-testid={`dispute-approve-${d.id}`}>
                    {busyId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white text-red-600 hover:text-red-700"
                    disabled={busyId === d.id} onClick={() => act(d.id, "reject")} data-testid={`dispute-reject-${d.id}`}>
                    <X className="h-4 w-4" /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
